/**
 * 公開後に料理・豆知識のIDを変えたときの、セーブデータの引き継ぎ表。
 *
 * セーブデータは「何番目の料理か」ではなく **料理のID** で図鑑を記録している。
 * そのため、
 *  - 並べ替え（レベルの入れ替え）だけなら、図鑑もトロフィーも何もずれない。
 *    メダル（到達レベル）は段数で判定するので、12段のままなら影響しない。
 *  - **IDを変えると**、旧IDで記録した図鑑・豆知識が「存在しない料理」として数えられなくなる。
 *    図鑑の解放数が減り、「◯◯制覇」「図鑑」系トロフィーの進み具合が下がる。
 *
 * そこで、公開済みの県でIDを変えたり料理を差し替えたりするときは、必ずここに書く。
 * 書き忘れは tests/idMigration.test.ts が落とす（tests/fixtures/publishedIds.json と突き合わせる）。
 *
 * 書き方:
 *  - `items` / `tips` … 旧ID → 新ID。旧IDで開いていた図鑑・豆知識を、新IDで開いた扱いにする。
 *      呼び名・表記の修正（同じ料理のまま ID を直す）はこちら。
 *      連鎖してよい（a → b、のちに b → c と書けば、a の記録も c に届く）。
 *  - `retiredItems` / `retiredTips` … 引き継がずに外すID。
 *      **別の料理に差し替えた**ときはこちらを基本にする。図鑑は「作った料理」の記録なので、
 *      作っていない料理を開いた扱いにはしない。
 *      旧IDの記録は保存データに残る（数えないだけ）。あとから `items` へ移せば復活する。
 *
 * 一度取ったトロフィーは、条件を満たさなくなっても消えない（TrophyBook）。
 * ここで守るのは、まだ取っていないトロフィーの進み具合と、図鑑の表示。
 *
 * 例:
 *   'hiroshima-food': {
 *     items: { 'hiroshima-yaki': 'hiroshima-okonomiyaki' },  // 呼称を県の公式に合わせた
 *     retiredItems: ['old-dish'],                            // 別の料理に差し替えた
 *     tips: { 'castle-tenshu': 'castle-honmaru' },
 *   },
 */

export interface StageIdMigration {
  /** 旧料理ID → 新料理ID（図鑑の解放・出会いを引き継ぐ）。 */
  items?: Readonly<Record<string, string>>;
  /** 引き継がずに外した料理ID。 */
  retiredItems?: readonly string[];
  /** 旧豆知識ID → 新豆知識ID（ホットスポットのID）。 */
  tips?: Readonly<Record<string, string>>;
  /** 引き継がずに外した豆知識ID。 */
  retiredTips?: readonly string[];
}

/** ステージID → 引き継ぎ表。 */
export const ID_MIGRATIONS: Readonly<Record<string, StageIdMigration>> = {
  'hokkaido-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['yubari-melon', 'butter-sand'],
  },
  'miyagi-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['zunda-edamame'],
  },
  'chiba-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['boshu-biwa', 'kujira-no-tare', 'futomaki-matsuri-zushi', 'fuukashi'],
  },
  'kagawa-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['olive', 'wasanbon', 'ibuki-iriko', 'tai-somen'],
  },
  'hyogo-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-05 香住ガニを淡路島の鱧（はもすき）に差し替えた（Lv10・11 が続けてかにだったため）
    retiredItems: ['tanba-kuro-daizu', 'ikanago-kugini', 'awaji-tamanegi', 'kasumi-gani'],
  },
  'yamagata-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['dongarajiru'],
  },
  'shizuoka-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['wasabizuke'],
  },
  'fukui-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['fukui-ume', 'oboro-konbu', 'kodai-sasazuke', 'decchi-youkan'],
  },
  'saga-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['marubouro', 'ogi-yokan', 'kanzaki-somen', 'suko-zushi', 'dabu'],
  },
  'nagasaki-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（tsushima-iriyaki-soba）
    retiredItems: ['kankoro-mochi', 'hikado', 'omura-zushi', 'iriyaki', 'tsushima-iriyaki-soba'],
  },
  'oita-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['kabosu', 'ishigaki-mochi'],
  },
  'miyazaki-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['nerikuri'],
  },
  'kagoshima-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['kokuto', 'tankan', 'getanha', 'akumaki'],
  },
  'kumamoto-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['tojiko-mame', 'yubeni', 'yakiayu-kanroni', 'tai-somen'],
  },
  'tottori-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['oiri', 'ago-chikuwa', 'tochi-mochi', 'kakinoha-zushi', 'itadaki', 'oyagani'],
  },
  'shimane-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['hoshigaki', 'sasamaki', 'oki-arame', 'kakuzushi', 'akagai-garanmushi', 'sazae-meshi'],
  },
  'okayama-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['kinira', 'yubeshi', 'shimizu-hakuto', 'tenobe-somen'],
  },
  'yamaguchi-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['natsumikan-gashi', 'uiro', 'chisha-namasu', 'gobo-maki', 'itoko-ni'],
  },
  'tokushima-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['awa-uiro', 'imomochi', 'boze-zushi', 'hirara-yaki'],
  },
  'ehime-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（fukumen）
    retiredItems: ['hino-kabu', 'igisu-dofu', 'tachi-maki', 'houraku-yaki', 'fukumen'],
  },
  'kochi-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['itadori', 'kawaebi', 'inaka-zushi', 'guruni', 'tosa-buntan'],
  },
  'kyoto-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['sansho-tsukudani', 'suguki', 'shibazuke', 'korokaki-namasu', 'kuromame-ni', 'tochi-mochi-kyoto', 'manganji', 'minazuki', 'kinome-ae', 'senmaizuke'],
  },
  'osaka-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['gaccho-karaage', 'wakagobo-ni', 'odamaki-mushi'],
  },
  'shiga-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['ebimame', 'chojifu', 'hinona', 'yaki-moroko', 'decchi-yokan-shiga', 'amenoio-gohan', 'oumigyu-misozuke'],
  },
  'nara-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（nara-chameshi）
    retiredItems: ['narazuke', 'shikishiki', 'dengara', 'imobota', 'nanairo-oae', 'yamato-zoni', 'nara-chameshi'],
  },
  'wakayama-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['kinzanji-miso', 'mikan-mochi', 'bouri', 'igami-nitsuke', 'nare-zushi', 'kokera-zushi'],
  },
  'aichi-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['ichijiku', 'iga-manju', 'oni-manju', 'oshimon', 'karimori-kasuzuke', 'moroko-zushi'],
  },
  'gifu-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['akakabu-zuke', 'myoga-bochi', 'karasumi', 'kuwanoki-mame'],
  },
  'mie-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['rakkasei-nimame', 'naisho-mochi', 'ahodaki', 'kounago-kugini', 'ibara-mochi', 'fuki-dawara', 'garagara-oroshi', 'same-namasu', 'arame-maki', 'sakana-no-jifu'],
  },
  'toyama-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['shiroebi-kakiage', 'bekko', 'yakitsuke', 'suzuki', 'kobumaki', 'buri-daikon'],
  },
  'ishikawa-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['takenoko-kombu', 'goshiki-namagashi', 'aimaze', 'suizen', 'daikon-zushi', 'kagafuto-kyuri', 'megisu-dangojiru', 'tai-no-karamushi'],
  },
  'niigata-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（kensan-yaki）
    retiredItems: ['fukashi-nasu', 'kurumafu-nimono', 'kakiae-namasu', 'igoneri', 'shoyu-okowa', 'kensan-yaki'],
  },
  'aomori-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['akakabu-senmaizuke', 'mame-shitogi', 'hoshi-mochi', 'nasu-shiso-maki', 'beko-mochi', 'kinka-mochi', 'keiran', 'tara-jappa-jiru'],
  },
  'iwate-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（kitakami-croquette）
    retiredItems: ['shidami-dango', 'kirisensho', 'ocha-mochi', 'soba-kakke', 'ganzuki', 'hyuzu', 'hizu-namasu', 'sukikonbu-nimono', 'kurumi-zoni', 'kitakami-croquette'],
  },
  'akita-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['kamabuku', 'tofu-castella', 'tamago-kanten', 'nata-zuke', 'akazuke', 'tonburi-nagaimo'],
  },
  'fukushima-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（shingoro）
    retiredItems: ['junen-botamochi', 'anpogaki-namasu', 'hatto', 'tsuyu-nishime', 'donko-jiru', 'shingoro'],
  },
  'ibaraki-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    // 2026-10-12 地元民レビューで差し替えた（hoshiimo-tempura）
    retiredItems: ['peanut-miso', 'shikinbai', 'ogura-renkon', 'tagane-mochi', 'shimi-konnyaku', 'paita-yaki', 'unagi-hobikini', 'iwashi-unohana', 'hoshiimo-tempura'],
  },
  'tochigi-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['komugi-manju', 'yuzu-maki', 'agemaki-yuba', 'bandai-mochi', 'imogushi', 'aiso-kanroni', 'ebi-daikon', 'kanpyo-tamagotoji', 'shimotsukare'],
  },
  'gunma-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['mayudama', 'hana-ingen', 'yakimochi', 'joshu-kinpira', 'negi-nuta', 'amaneji', 'koshine-jiru'],
  },
  'saitama-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['kuwai-fukumeni', 'shio-anbin', 'neji', 'tsutokko', 'shoden-zushi', 'katemeshi', 'gojiru'],
  },
  'tokyo-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['tsukudani', 'jisuke-imo', 'kohada-awazuke', 'sakura-mochi', 'imo-yokan', 'bettara-zuke'],
  },
  'kanagawa-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['ishigaki-dango', 'kanko-yaki', 'herahera-dango', 'sake-manju', 'norabona', 'kenchin-jiru', 'maguro-kabutoyaki'],
  },
  'yamanashi-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['koshu-koume', 'seida-no-tamaji', 'tsuki-no-shizuku', 'oshaka-kogori', 'akebono-edamame', 'urajiro-manju', 'anbin', 'usuyaki', 'memaki'],
  },
  'nagano-food': {
    // 2026-10-02 確定案で別の料理に差し替えた
    retiredItems: ['nota-mochi', 'yashouma', 'tenyose', 'sunki-zuke', 'hoba-maki'],
  },
};

export function idMigrationFor(stageId: string): StageIdMigration | undefined {
  return ID_MIGRATIONS[stageId];
}

/**
 * 旧IDを最新のIDへたどる（a → b → c）。表に無ければそのまま返す。
 * 表が輪になっていても止まる（テストで輪は禁止している）。
 */
export function resolveId(map: Readonly<Record<string, string>> | undefined, id: string): string {
  if (!map) return id;
  let current = id;
  const visited = new Set<string>([current]);
  while (Object.prototype.hasOwnProperty.call(map, current)) {
    const next = map[current];
    if (visited.has(next)) break;
    visited.add(next);
    current = next;
  }
  return current;
}

/** ID一覧を引き継ぎ表で置き換える（重複は1つにまとめ、順番は保つ）。 */
export function migrateIdList(
  ids: readonly string[],
  map: Readonly<Record<string, string>> | undefined,
): string[] {
  if (!map) return [...ids];
  return [...new Set(ids.map((id) => resolveId(map, id)))];
}
